import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, ArrowRight, RefreshCcw, Check, Sparkles, Loader2, X,
    Monitor, Tablet as TabletIcon, Smartphone as SmartphoneIcon, Mail, Phone,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* =========================================================
   Premium light-glass field styling (matches Contact form)
   ========================================================= */
const fieldClass = 'contact-input w-full rounded-xl px-4 py-3 text-[#1A1A2E] focus:outline-none transition-all duration-200';

const FIELD_GROUPS = [
    {
        title: 'About your business',
        fields: [
            { key: 'business_name',  label: 'Business name *',           type: 'text', placeholder: 'Acme Tea', required: true },
            { key: 'business_type',  label: 'Business type *',           type: 'text', placeholder: 'Local tea shop, SaaS startup, consultancy…', required: true },
            { key: 'website_goal',   label: 'Main website goal *',       type: 'textarea', placeholder: 'Get more local customers, online orders, bookings…', required: true, rows: 3 },
        ],
    },
    {
        title: 'Look & feel',
        fields: [
            { key: 'preferred_style',   label: 'Preferred style',     type: 'text', placeholder: 'Minimal, warm & friendly, bold modern, premium dark…' },
            { key: 'preferred_colours', label: 'Preferred colours',   type: 'text', placeholder: 'Navy + cream, earthy green, vibrant blue…' },
            { key: 'reference_websites',label: 'Reference websites',  type: 'text', placeholder: 'apple.com, linear.app …' },
        ],
    },
    {
        title: 'Content',
        fields: [
            { key: 'required_pages',    label: 'Pages you need',          type: 'text',     placeholder: 'home, services, about, contact' },
            { key: 'services_products', label: 'Services / products',     type: 'textarea', placeholder: 'List the main things you sell or offer', rows: 4 },
            { key: 'text_ideas',        label: 'Text / copy ideas',       type: 'textarea', placeholder: 'Any taglines, story, key facts you want included', rows: 4 },
        ],
    },
    {
        title: 'Brand & contact',
        fields: [
            { key: 'logo_data_url',  label: 'Logo (optional)',       type: 'file' },
            { key: 'contact_email',  label: 'Your email',            type: 'email', placeholder: 'you@business.co.uk' },
            { key: 'contact_phone',  label: 'Your phone (optional)', type: 'tel',   placeholder: '+44 7700 900000' },
        ],
    },
];

const EMPTY_FORM = Object.fromEntries(
    FIELD_GROUPS.flatMap((g) => g.fields).map((f) => [f.key, ''])
);

const fileToDataUrl = (file) =>
    new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
    });

/* =========================================================
   Main component
   ========================================================= */
const WebsiteDemo = () => {
    const [stage, setStage] = useState('form'); // form | generating | preview
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [demo, setDemo] = useState(null);
    const [approveState, setApproveState] = useState('idle'); // idle | working | approved
    const [publishState, setPublishState] = useState('idle'); // idle | working | not_configured | published | error
    const [publishResult, setPublishResult] = useState(null); // { liveUrl?, message?, missing?: [] }
    const [leadOpen, setLeadOpen] = useState(false);
    const [lead, setLead] = useState({ name: '', email: '', phone: '' });
    const [leadError, setLeadError] = useState('');
    const [genSlow, setGenSlow] = useState(false); // true once generation has been running >60s

    // Reset scroll on stage change
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [stage]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const validateStep = () => {
        const group = FIELD_GROUPS[step];
        for (const f of group.fields) {
            if (f.required && !form[f.key]?.trim()) {
                setError(`Please fill in: ${f.label.replace(' *', '')}`);
                return false;
            }
        }
        setError('');
        return true;
    };

    const handleNext = () => {
        if (!validateStep()) return;
        if (step < FIELD_GROUPS.length - 1) setStep((s) => s + 1);
        else submitForm();
    };

    const submitForm = async (regenerate = false) => {
        setStage('generating');
        setError('');
        setGenSlow(false);
        setApproveState('idle');
        setPublishState('idle');
        setPublishResult(null);
        // After 60s of generation, surface a "this is taking longer" hint to the user.
        const slowTimer = setTimeout(() => setGenSlow(true), 60_000);
        try {
            const payload = { ...form };
            if (!payload.contact_email) delete payload.contact_email;
            const { data } = await axios.post(`${API}/generate-website-demo`, payload, { timeout: 120_000 });
            setDemo(data);
            setLead({
                name: form.business_name?.trim() || '',
                email: form.contact_email?.trim() || '',
                phone: form.contact_phone?.trim() || '',
            });
            setStage('preview');
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Generation failed');
            setStage('error');
        } finally {
            clearTimeout(slowTimer);
            setGenSlow(false);
        }
        // silence unused-param warning
        void regenerate;
    };

    const handleApprove = () => {
        if (!demo?.demo_id) return;
        // If we already saved the lead earlier, just confirm — no extra modal.
        if (approveState === 'approved') return;
        setLeadError('');
        setLeadOpen(true);
    };

    const submitLeadAndApprove = async () => {
        if (!demo?.demo_id) return;
        const name = lead.name.trim();
        const email = lead.email.trim();
        const phone = lead.phone.trim();
        if (!name) { setLeadError('Please add your name.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setLeadError('Please enter a valid email address.');
            return;
        }
        setLeadError('');
        setApproveState('working');
        try {
            await axios.post(`${API}/demo/save`, {
                demo_id: demo.demo_id,
                user_name: name,
                user_email: email,
                user_phone: phone || null,
            });
            setApproveState('approved');
            setLeadOpen(false);
        } catch (e) {
            setApproveState('idle');
            setLeadError(e?.response?.data?.detail || e.message || 'Could not save — please try again.');
        }
    };

    const handlePublish = async () => {
        if (!demo?.demo_id) return;
        setPublishState('working');
        setPublishResult(null);
        setError('');
        try {
            const { data } = await axios.post(
                `${API}/publish-website-demo`,
                { demo_id: demo.demo_id },
                { timeout: 90_000 },
            );
            if (data?.status === 'published') {
                setPublishState('published');
                setPublishResult({ liveUrl: data.liveUrl, repoUrl: data.repo_url });
            } else if (data?.status === 'not_configured') {
                setPublishState('not_configured');
                setPublishResult({ message: data.message, missing: data.missing || [] });
            } else {
                setPublishState('error');
                setPublishResult({ message: data?.message || 'Unexpected response from the server.' });
            }
        } catch (e) {
            setPublishState('error');
            setPublishResult({
                message: e?.response?.data?.detail || e.message || 'Publishing failed',
            });
        }
    };

    return (
        <main
            className="min-h-screen text-[#1A1A2E] pb-24 pt-32"
            data-testid="website-demo-page"
            style={{
                background:
                    'radial-gradient(60% 50% at 80% 0%, rgba(56,189,248,0.10) 0%, transparent 70%),' +
                    'radial-gradient(50% 45% at 10% 100%, rgba(99,102,241,0.10) 0%, transparent 70%),' +
                    'linear-gradient(180deg, #EAF2FE 0%, #F4F8FF 45%, #FFFFFF 100%)',
            }}
        >
            <div className={`mx-auto px-6 lg:px-8 transition-[max-width] duration-300 ${stage === 'preview' ? 'max-w-[88vw]' : 'max-w-5xl'}`}>
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/85 backdrop-blur-md border border-[#DBE6FF] shadow-[0_4px_16px_rgba(37,99,235,0.10)]">
                        <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                        <span className="font-mono text-[10.5px] tracking-[0.18em] text-[#0F172A] uppercase font-semibold">
                            AI Website Planner
                        </span>
                    </div>
                    <h1 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.12]">
                        Watch your website appear <span className="text-[#2563EB]">in 60 seconds</span>.
                    </h1>
                    <p className="mt-4 text-[16px] text-[#4A4A5A] leading-relaxed max-w-xl mx-auto">
                        Answer a few quick questions. Our AI builds a live HTML &amp; CSS preview of your
                        website — accept it, tweak it, regenerate, or take it as a starting point.
                    </p>
                    <Link
                        to="/"
                        className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-[#4A4A5A] hover:text-[#2563EB] transition-colors"
                        data-testid="demo-back-home"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Back to homepage
                    </Link>
                </div>

                <AnimatePresence mode="wait">
                    {stage === 'form' && (
                        <FormStage
                            key="form"
                            step={step}
                            form={form}
                            update={update}
                            error={error}
                            onPrev={() => setStep((s) => Math.max(0, s - 1))}
                            onNext={handleNext}
                        />
                    )}
                    {stage === 'generating' && (
                        <GeneratingStage
                            key="gen"
                            slow={genSlow}
                            onRegenerate={() => submitForm(true)}
                        />
                    )}
                    {stage === 'error' && (
                        <ErrorStage
                            key="err"
                            error={error}
                            onRetry={() => submitForm(true)}
                            onBackToForm={() => { setStage('form'); setError(''); }}
                        />
                    )}
                    {stage === 'preview' && demo && (
                        <PreviewStage
                            key="preview"
                            demo={demo}
                            approveState={approveState}
                            error={error}
                            onApprove={handleApprove}
                            onRegenerate={() => submitForm(true)}
                            onBackToForm={() => { setStage('form'); setError(''); }}
                        />
                    )}
                </AnimatePresence>
            </div>

            <AnimatePresence>
                {leadOpen && (
                    <LeadCaptureModal
                        key="lead-modal"
                        lead={lead}
                        setLead={setLead}
                        error={leadError}
                        submitting={approveState === 'working'}
                        onSubmit={submitLeadAndApprove}
                        onClose={() => {
                            if (approveState === 'working') return;
                            setLeadOpen(false);
                            setLeadError('');
                        }}
                    />
                )}
            </AnimatePresence>
        </main>
    );
};

/* =========================================================
   Form stage — multi-step questionnaire
   ========================================================= */
const FormStage = ({ step, form, update, error, onPrev, onNext }) => {
    const group = FIELD_GROUPS[step];
    const fileInputRef = useRef(null);

    const handleFile = async (key, file) => {
        if (!file) { update(key, ''); return; }
        if (file.size > 1_500_000) {
            update(key, '');
            alert('Logo must be smaller than 1.5 MB');
            return;
        }
        const dataUrl = await fileToDataUrl(file);
        update(key, dataUrl);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-[24px] p-6 md:p-10"
            style={{
                background: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                boxShadow:
                    '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
            data-testid="demo-form-card"
        >
            {/* Stepper */}
            <div className="flex items-center gap-1.5 mb-6">
                {FIELD_GROUPS.map((_, i) => (
                    <div
                        key={i}
                        className="flex-1 h-1 rounded-full transition-colors"
                        style={{ background: i <= step ? '#2563EB' : 'rgba(15,23,42,0.10)' }}
                    />
                ))}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#64748B] mb-1">
                Step {step + 1} / {FIELD_GROUPS.length}
            </div>
            <h2 className="font-display text-2xl font-semibold text-[#0F172A] mb-6">
                {group.title}
            </h2>

            <div className="space-y-4">
                {group.fields.map((f) => {
                    const value = form[f.key] ?? '';
                    if (f.type === 'textarea') {
                        return (
                            <div key={f.key}>
                                <label className="block text-[13px] font-medium text-[#0F172A] mb-1.5">{f.label}</label>
                                <textarea
                                    rows={f.rows || 3}
                                    className={fieldClass}
                                    placeholder={f.placeholder}
                                    value={value}
                                    onChange={(e) => update(f.key, e.target.value)}
                                    data-testid={`demo-input-${f.key}`}
                                />
                            </div>
                        );
                    }
                    if (f.type === 'file') {
                        return (
                            <div key={f.key}>
                                <label className="block text-[13px] font-medium text-[#0F172A] mb-1.5">{f.label}</label>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="contact-input px-4 py-2.5 text-[13px] font-medium hover:!bg-white/95 cursor-pointer"
                                        data-testid={`demo-input-${f.key}`}
                                    >
                                        {value ? 'Replace logo' : 'Choose file'}
                                    </button>
                                    {value && (
                                        <>
                                            <img src={value} alt="logo preview" className="h-10 w-auto rounded border border-[#E8E8EC]" />
                                            <button
                                                type="button"
                                                onClick={() => update(f.key, '')}
                                                className="text-[12px] text-[#64748B] hover:text-[#0F172A]"
                                            >Remove</button>
                                        </>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFile(f.key, e.target.files?.[0])}
                                />
                            </div>
                        );
                    }
                    return (
                        <div key={f.key}>
                            <label className="block text-[13px] font-medium text-[#0F172A] mb-1.5">{f.label}</label>
                            <input
                                type={f.type}
                                className={fieldClass}
                                placeholder={f.placeholder}
                                value={value}
                                onChange={(e) => update(f.key, e.target.value)}
                                data-testid={`demo-input-${f.key}`}
                            />
                        </div>
                    );
                })}
            </div>

            {error && (
                <div className="mt-5 text-[13px] text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-3 py-2" data-testid="demo-error">
                    {error}
                </div>
            )}

            <div className="mt-7 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onPrev}
                    disabled={step === 0}
                    className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="demo-prev"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                    type="button"
                    onClick={onNext}
                    className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-[15px] px-6 py-3 rounded-full"
                    style={{ boxShadow: '0 4px 15px rgba(59,130,246,0.30), 0 8px 30px rgba(59,130,246,0.15)' }}
                    data-testid="demo-next"
                >
                    {step === FIELD_GROUPS.length - 1 ? 'Generate my website' : 'Next'}
                    <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
};

/* =========================================================
   Generating stage — premium 3D cube + progress timeline
   ========================================================= */
const PROGRESS_STEPS = [
    { delaySec: 0,  label: 'Analysing your business' },
    { delaySec: 3,  label: 'Designing layout' },
    { delaySec: 6,  label: 'Building pages' },
    { delaySec: 9,  label: 'Adding styling' },
    { delaySec: 12, label: 'Almost ready', isFinal: true },
];

const GeneratingStage = ({ slow, onRegenerate }) => {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const start = Date.now();
        const t = setInterval(() => setElapsed((Date.now() - start) / 1000), 250);
        return () => clearInterval(t);
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-[24px] px-6 py-12 sm:px-10 sm:py-14 text-center mx-auto"
            style={{
                maxWidth: 640,
                background: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
            data-testid="demo-generating"
        >
            {/* 3D rotating cube with pulsing glow */}
            <div className="demo-cube-stage" aria-hidden="true">
                <div className="demo-cube-glow" />
                <div className="demo-cube">
                    <div className="demo-cube-face demo-cube-face--front" />
                    <div className="demo-cube-face demo-cube-face--back" />
                    <div className="demo-cube-face demo-cube-face--right" />
                    <div className="demo-cube-face demo-cube-face--left" />
                    <div className="demo-cube-face demo-cube-face--top" />
                    <div className="demo-cube-face demo-cube-face--bottom" />
                </div>
            </div>

            <h3 className="font-display text-2xl sm:text-[26px] font-semibold text-[#0F172A] mt-8">
                Generating your website
                <span className="demo-loading-dots" aria-hidden="true">
                    <span>.</span><span>.</span><span>.</span>
                </span>
            </h3>
            <p className="mt-2 text-[14px] text-[#475569]">
                Our AI is writing HTML &amp; CSS, choosing a palette and laying out the
                sections. This usually takes 20-60 seconds.
            </p>

            <ul className="mt-8 mx-auto max-w-md text-left space-y-2.5" data-testid="demo-progress-steps">
                {PROGRESS_STEPS.map((step, i) => {
                    const active = elapsed >= step.delaySec;
                    return (
                        <motion.li
                            key={step.label}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: active ? 1 : 0.25, y: active ? 0 : 6 }}
                            transition={{ duration: 0.35, ease: 'easeOut' }}
                            className="flex items-center gap-3 text-[14px]"
                            data-testid={`demo-progress-step-${i + 1}`}
                        >
                            <span
                                className={`inline-flex w-6 h-6 rounded-full items-center justify-center shrink-0 ${
                                    active
                                        ? step.isFinal
                                            ? 'bg-[#FEF3C7] text-[#92400E]'
                                            : 'bg-[#D1FAE5] text-[#065F46]'
                                        : 'bg-[#E2E8F0] text-[#94A3B8]'
                                }`}
                            >
                                {active && step.isFinal ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : active ? (
                                    <Check className="w-3.5 h-3.5" />
                                ) : (
                                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                )}
                            </span>
                            <span className={active ? 'text-[#0F172A] font-medium' : 'text-[#94A3B8]'}>
                                {step.label}
                                {active ? (step.isFinal ? '…' : '') : '…'}
                            </span>
                        </motion.li>
                    );
                })}
            </ul>

            {slow && (
                <div
                    className="mt-8 mx-auto max-w-md rounded-2xl bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 text-left"
                    data-testid="demo-generating-slow"
                >
                    <p className="text-[13.5px] text-[#92400E] leading-relaxed">
                        This is taking longer than usual. Please wait or try again.
                    </p>
                    <button
                        type="button"
                        onClick={onRegenerate}
                        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-[#92400E] bg-white border border-[#FCD34D]"
                        data-testid="demo-generating-regenerate"
                    >
                        <RefreshCcw className="w-3.5 h-3.5" /> Regenerate
                    </button>
                </div>
            )}
        </motion.div>
    );
};

/* =========================================================
   Error stage — generation failed
   ========================================================= */
const ErrorStage = ({ error, onRetry, onBackToForm }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="rounded-[24px] px-6 py-12 sm:px-10 sm:py-14 text-center mx-auto"
        style={{
            maxWidth: 560,
            background: 'rgba(255, 255, 255, 0.55)',
            backdropFilter: 'blur(20px) saturate(140%)',
            WebkitBackdropFilter: 'blur(20px) saturate(140%)',
            border: '1.5px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
        }}
        data-testid="demo-error"
    >
        <div className="mx-auto inline-flex w-14 h-14 rounded-full items-center justify-center bg-[#FEE2E2] text-[#B91C1C] mb-5">
            <X className="w-7 h-7" strokeWidth={2.2} />
        </div>
        <h3 className="font-display text-[22px] sm:text-[24px] font-semibold text-[#0F172A]">
            Something went wrong
        </h3>
        <p className="mt-2 text-[14px] text-[#475569] leading-relaxed">
            Please try again — your answers are still saved.
        </p>
        {error && (
            <p className="mt-3 mx-auto max-w-md text-[12.5px] text-[#94A3B8] font-mono break-words">
                {error}
            </p>
        )}
        <div className="mt-6 flex flex-col sm:flex-row gap-2.5 justify-center">
            <button
                type="button"
                onClick={onBackToForm}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#DBE6FF] bg-white/70"
                data-testid="demo-error-back"
            >
                <ArrowLeft className="w-4 h-4" /> Back to form
            </button>
            <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8]"
                style={{ boxShadow: '0 4px 15px rgba(59,130,246,0.30)' }}
                data-testid="demo-error-retry"
            >
                <RefreshCcw className="w-4 h-4" /> Try again
            </button>
        </div>
    </motion.div>
);

/* =========================================================
   Preview stage — iframe + actions
   ========================================================= */
/* =========================================================
   Preview stage — iframe + actions
   ========================================================= */
const DEVICE_PRESETS = {
    desktop: { label: 'Desktop', icon: Monitor, frameMaxWidth: '75vw', iframeWidth: '100%', iframeHeight: '70vh',     testId: 'demo-device-desktop' },
    tablet:  { label: 'Tablet',  icon: TabletIcon,  frameMaxWidth: 820,    iframeWidth: 768,    iframeHeight: '70vh',  testId: 'demo-device-tablet'  },
    mobile:  { label: 'Mobile',  icon: SmartphoneIcon, frameMaxWidth: 430, iframeWidth: 375,    iframeHeight: '70vh',  testId: 'demo-device-mobile'  },
};

const scrollToHash = (id) => (e) => {
    e.preventDefault();
    // The homepage uses one-page anchors; pricing/contact/etc. also exist as standalone routes.
    const target = document.getElementById(id);
    if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
        window.location.assign(id === 'pricing' ? '/#pricing' : '/contact');
    }
};

const PreviewStage = ({
    demo,
    approveState,
    error,
    onApprove,
    onRegenerate,
    onBackToForm,
}) => {
    const [device, setDevice] = useState('desktop');
    const preset = DEVICE_PRESETS[device];

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-[24px] p-5 sm:p-6"
            style={{
                background: 'rgba(255, 255, 255, 0.55)',
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            }}
            data-testid="demo-preview"
        >
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#64748B]">
                        Live preview · {demo.status}
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#0F172A] mt-0.5">
                        Your AI-generated website
                    </h3>
                </div>
                {demo.sections?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 max-w-md">
                        {demo.sections.slice(0, 8).map((s, i) => (
                            <span
                                key={i}
                                className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#EEF4FF] text-[#2563EB] border border-[#DBE6FF]"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {demo.summary && (
                <p className="text-[13.5px] text-[#475569] mb-4 leading-relaxed">
                    {demo.summary}
                </p>
            )}

            {/* Device-switcher tabs */}
            <div
                className="inline-flex p-1 rounded-full bg-white/80 border border-[#DBE6FF] mb-4 gap-1"
                role="tablist"
                aria-label="Preview device size"
                data-testid="demo-device-tabs"
            >
                {Object.entries(DEVICE_PRESETS).map(([key, p]) => {
                    const Icon = p.icon;
                    const active = device === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setDevice(key)}
                            data-testid={p.testId}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                                active
                                    ? 'bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white shadow-[0_4px_12px_rgba(37,99,235,0.30)]'
                                    : 'text-[#475569] hover:text-[#0F172A]'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {p.label}
                        </button>
                    );
                })}
            </div>

            {/* Device frame — preview at 75vw on desktop with native widths on tablet/mobile */}
            <div
                className="mx-auto w-full transition-[max-width] duration-300 ease-out"
                style={{
                    maxWidth: typeof preset.frameMaxWidth === 'number'
                        ? `${preset.frameMaxWidth}px`
                        : preset.frameMaxWidth,
                }}
                data-testid="demo-device-frame"
            >
                <div
                    className="rounded-2xl bg-[#1E293B] p-2.5 sm:p-3"
                    style={{ boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22), 0 8px 24px rgba(15, 23, 42, 0.12)' }}
                >
                    {/* Faux browser/device chrome */}
                    <div className="flex items-center gap-1.5 px-2 pb-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FCA5A5]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FCD34D]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#86EFAC]" />
                        <div className="ml-2 flex-1 h-5 rounded-md bg-[#0F172A]/40 border border-white/10 flex items-center px-2">
                            <span className="font-mono text-[10.5px] text-white/60 truncate">
                                preview · {preset.label.toLowerCase()}
                            </span>
                        </div>
                    </div>

                    {/* The iframe scrolls internally so the user can browse the entire site. */}
                    <div
                        className="overflow-hidden bg-white rounded-lg flex justify-center"
                        style={{ height: '70vh', minHeight: 420 }}
                    >
                        <iframe
                            title="Generated website preview"
                            src={`${BACKEND}${demo.preview_url}`}
                            sandbox="allow-same-origin"
                            data-testid="demo-iframe"
                            className="border-0"
                            style={{
                                width: typeof preset.iframeWidth === 'number'
                                    ? `${preset.iframeWidth}px`
                                    : preset.iframeWidth,
                                height: '100%',
                                maxWidth: '100%',
                            }}
                        />
                    </div>
                </div>
            </div>

            {error && (
                <div className="mt-4 text-[13px] text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-3 py-2">
                    {error}
                </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5">
                <button
                    type="button"
                    onClick={onBackToForm}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#DBE6FF] bg-white/70"
                    data-testid="demo-back-to-form"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                    type="button"
                    onClick={onRegenerate}
                    disabled={approveState === 'working'}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[14px] font-semibold text-[#0F172A] border border-[#DBE6FF] bg-white/90 hover:bg-white"
                    data-testid="demo-regenerate"
                >
                    <RefreshCcw className="w-4 h-4" /> Regenerate
                </button>
                <button
                    type="button"
                    onClick={onApprove}
                    disabled={approveState !== 'idle'}
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] disabled:opacity-60"
                    style={{ boxShadow: '0 4px 15px rgba(59,130,246,0.30), 0 8px 30px rgba(59,130,246,0.15)' }}
                    data-testid="demo-approve"
                >
                    {approveState === 'approved' ? (
                        <><Check className="w-4 h-4" /> Approved</>
                    ) : approveState === 'working' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    ) : (
                        <><Check className="w-4 h-4" /> Accept design</>
                    )}
                </button>
            </div>

            {approveState === 'approved' && (
                <div
                    className="mt-5 flex items-start gap-2.5 text-[13.5px] text-[#065F46] bg-[#D1FAE5] border border-[#6EE7B7] rounded-2xl px-4 py-3"
                    data-testid="demo-approved-confirmation"
                >
                    <Check className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="leading-relaxed">
                        <span className="font-semibold">Design approved.</span> We saved your demo and will prepare the next step.
                    </div>
                </div>
            )}

            {approveState === 'approved' && (
                <div
                    className="mt-5 rounded-2xl border border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] to-white px-5 py-5 sm:px-6 sm:py-6"
                    data-testid="demo-next-step-card"
                >
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#2563EB]">
                        Next step
                    </div>
                    <h4 className="font-display text-[18px] sm:text-[19px] font-semibold text-[#0F172A] mt-1">
                        What happens next?
                    </h4>

                    <ul className="mt-4 space-y-2.5">
                        <li className="flex items-start gap-2.5 text-[14px] text-[#0F172A]">
                            <span className="inline-flex w-6 h-6 rounded-full items-center justify-center bg-[#D1FAE5] text-[#065F46] shrink-0 mt-0.5">
                                <Check className="w-3.5 h-3.5" />
                            </span>
                            <span>Your design has been saved.</span>
                        </li>
                        <li className="flex items-start gap-2.5 text-[14px] text-[#0F172A]">
                            <span className="inline-flex w-6 h-6 rounded-full items-center justify-center bg-[#DBE6FF] text-[#2563EB] shrink-0 mt-0.5">
                                <Mail className="w-3.5 h-3.5" />
                            </span>
                            <span>We'll email you within 24 hours with a detailed quote.</span>
                        </li>
                        <li className="flex items-start gap-2.5 text-[14px] text-[#0F172A]">
                            <span className="inline-flex w-6 h-6 rounded-full items-center justify-center bg-[#FFE4E6] text-[#BE123C] shrink-0 mt-0.5">
                                <Phone className="w-3.5 h-3.5" />
                            </span>
                            <span>Or call us now to discuss your project.</span>
                        </li>
                    </ul>

                    <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
                        <a
                            href="/contact"
                            onClick={scrollToHash('contact')}
                            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8]"
                            style={{ boxShadow: '0 4px 15px rgba(59,130,246,0.30), 0 8px 30px rgba(59,130,246,0.15)' }}
                            data-testid="demo-next-step-contact"
                        >
                            Contact us <ArrowRight className="w-4 h-4" />
                        </a>
                        <a
                            href="/#pricing"
                            onClick={scrollToHash('pricing')}
                            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-semibold text-[#0F172A] border border-[#DBE6FF] bg-white/90 hover:bg-white"
                            data-testid="demo-next-step-pricing"
                        >
                            View packages <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

/* =========================================================
   Lead capture modal — shown when the user clicks Accept design
   ========================================================= */
const LeadCaptureModal = ({ lead, setLead, error, submitting, onSubmit, onClose }) => {
    const set = (k) => (e) => setLead((p) => ({ ...p, [k]: e.target.value }));

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6"
            data-testid="demo-lead-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-modal-title"
        >
            {/* Backdrop */}
            <button
                type="button"
                onClick={onClose}
                aria-label="Close lead capture"
                className="absolute inset-0 bg-[#0F172A]/45 backdrop-blur-[2px]"
                data-testid="demo-lead-modal-backdrop"
            />

            {/* Card */}
            <motion.div
                initial={{ y: 24, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 12, opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full sm:max-w-md rounded-[24px] p-6 sm:p-7"
                style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                    border: '1.5px solid rgba(255, 255, 255, 0.7)',
                    boxShadow:
                        '0 24px 60px rgba(15,23,42,0.20), 0 8px 24px rgba(37,99,235,0.14), 0 0 0 1px rgba(255,255,255,0.4) inset',
                }}
            >
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close"
                    className="absolute top-3.5 right-3.5 p-1.5 rounded-lg text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] disabled:opacity-40"
                    data-testid="demo-lead-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EEF4FF] border border-[#DBE6FF]">
                    <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
                    <span className="font-mono text-[10.5px] tracking-[0.18em] text-[#0F172A] uppercase font-semibold">
                        One more step
                    </span>
                </div>

                <h3
                    id="lead-modal-title"
                    className="mt-3 font-display text-[20px] sm:text-[22px] font-semibold text-[#0F172A] leading-snug"
                >
                    Where should we send your demo and project estimate?
                </h3>
                <p className="mt-1.5 text-[13.5px] text-[#475569] leading-relaxed">
                    We'll save your approved design and follow up with a short, no-pressure plan.
                </p>

                <div className="mt-5 space-y-3.5">
                    <div>
                        <label className="block text-[12.5px] font-medium text-[#0F172A] mb-1">Name *</label>
                        <input
                            type="text"
                            className={fieldClass}
                            placeholder="Your full name"
                            value={lead.name}
                            onChange={set('name')}
                            data-testid="demo-lead-name"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-[12.5px] font-medium text-[#0F172A] mb-1">Email *</label>
                        <input
                            type="email"
                            className={fieldClass}
                            placeholder="you@business.co.uk"
                            value={lead.email}
                            onChange={set('email')}
                            data-testid="demo-lead-email"
                        />
                    </div>
                    <div>
                        <label className="block text-[12.5px] font-medium text-[#0F172A] mb-1">Phone <span className="text-[#94A3B8] font-normal">(optional)</span></label>
                        <input
                            type="tel"
                            className={fieldClass}
                            placeholder="+44 7700 900000"
                            value={lead.phone}
                            onChange={set('phone')}
                            data-testid="demo-lead-phone"
                        />
                    </div>
                </div>

                {error && (
                    <div
                        className="mt-3 text-[13px] text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-3 py-2"
                        data-testid="demo-lead-error"
                    >
                        {error}
                    </div>
                )}

                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-[15px] px-6 py-3 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ boxShadow: '0 6px 22px rgba(37,99,235,0.35), 0 12px 40px rgba(37,99,235,0.18)' }}
                    data-testid="demo-lead-submit"
                >
                    {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    ) : (
                        <><Check className="w-4 h-4" /> Approve design</>
                    )}
                </button>

                <p className="mt-3 text-[11.5px] text-[#94A3B8] text-center leading-relaxed">
                    We only use your details to send your demo and follow up about your project. No spam, no third parties.
                </p>
            </motion.div>
        </motion.div>
    );
};

export default WebsiteDemo;
